const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again."
const NETWORK_ERROR_MESSAGE =
  "We couldn't reach Coval. Check your connection and try again."
const MAX_VALIDATION_MESSAGES = 3

interface UserFacingErrorOptions {
  detailMessage?: boolean
  fallback?: string
  fieldLabels?: Readonly<Record<string, string>>
}

interface ErrorLikeRecord {
  detail?: unknown
  loc?: unknown
  message?: unknown
  msg?: unknown
  name?: unknown
  status?: unknown
}

function isErrorRecord(error: unknown): error is ErrorLikeRecord {
  return typeof error === "object" && error !== null
}

function getStatus(error: unknown) {
  if (!isErrorRecord(error) || typeof error.status !== "number") {
    return undefined
  }

  return error.status
}

function getDetailCode(error: unknown) {
  const detail = getDetail(error)
  if (!detail || typeof detail.code !== "string") {
    return undefined
  }

  return detail.code
}

function getDetail(error: unknown): Record<string, unknown> | undefined {
  if (
    !isErrorRecord(error) ||
    typeof error.detail !== "object" ||
    error.detail === null
  ) {
    return undefined
  }

  return error.detail as Record<string, unknown>
}

function getDetailMessage(error: unknown) {
  const detail = getDetail(error)
  if (!detail || typeof detail.message !== "string") {
    return undefined
  }

  const message = detail.message.trim()
  return message || undefined
}

function humanizeFieldName(value: string) {
  const words = value.replaceAll("_", " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function formatLocation(
  value: unknown,
  fieldLabels: Readonly<Record<string, string>>,
) {
  if (!Array.isArray(value)) {
    return undefined
  }

  const segments = value.filter(
    (segment): segment is number | string =>
      typeof segment === "number" || typeof segment === "string",
  )
  if (segments.length === 0) {
    return undefined
  }

  return segments
    .map((segment) => {
      if (typeof segment === "number") {
        return `Item ${segment + 1}`
      }
      return fieldLabels[segment] ?? humanizeFieldName(segment)
    })
    .join(" > ")
}

function formatValidationDetail(
  value: unknown,
  fieldLabels: Readonly<Record<string, string>>,
) {
  if (!isErrorRecord(value) || typeof value.msg !== "string") {
    return undefined
  }

  const message = value.msg.trim()
  if (!message) {
    return undefined
  }

  const location = formatLocation(value.loc, fieldLabels)
  return location ? `${location}: ${message}` : message
}

function getValidationDetailsMessage(
  error: unknown,
  fieldLabels: Readonly<Record<string, string>>,
) {
  const detail = getDetail(error)
  if (!detail || !Array.isArray(detail.details)) {
    return undefined
  }

  const messages = detail.details
    .map((value) => formatValidationDetail(value, fieldLabels))
    .filter((message): message is string => message !== undefined)
  if (messages.length === 0) {
    return undefined
  }

  const visibleMessages = messages.slice(0, MAX_VALIDATION_MESSAGES)
  const remainingCount = messages.length - visibleMessages.length
  const suffix =
    remainingCount > 0
      ? ` ${remainingCount} more validation ${remainingCount === 1 ? "error" : "errors"}.`
      : ""

  return `${visibleMessages.join("; ")}${suffix}`
}

function getErrorName(error: unknown) {
  if (!isErrorRecord(error) || typeof error.name !== "string") {
    return undefined
  }

  return error.name
}

function getErrorMessage(error: unknown) {
  if (!isErrorRecord(error) || typeof error.message !== "string") {
    return undefined
  }

  return error.message
}

function normalizeCode(code?: string) {
  return code?.trim().toUpperCase()
}

function getMessageForErrorCode(code?: string) {
  const normalizedCode = normalizeCode(code)
  if (!normalizedCode) {
    return undefined
  }

  if (
    normalizedCode.includes("AUTH") ||
    normalizedCode.includes("UNAUTHORIZED")
  ) {
    return "Your session has expired. Sign in again to continue."
  }

  if (
    normalizedCode.includes("FORBIDDEN") ||
    normalizedCode.includes("PERMISSION")
  ) {
    return "You don't have permission to do that."
  }

  if (normalizedCode.includes("NOT_FOUND")) {
    return "We couldn't find that item. It may have been deleted or you may not have access."
  }

  if (normalizedCode.includes("CONFLICT")) {
    return "That item changed before we could save your update. Refresh and try again."
  }

  if (
    normalizedCode.includes("RATE_LIMIT") ||
    normalizedCode.includes("TOO_MANY_REQUESTS")
  ) {
    return "You're doing that a little too quickly. Wait a moment and try again."
  }

  if (
    normalizedCode.includes("VALIDATION") ||
    normalizedCode.includes("INVALID") ||
    normalizedCode.includes("BAD_REQUEST")
  ) {
    return "Some information was invalid. Check your input and try again."
  }

  return undefined
}

function getMessageForStatus(status?: number) {
  if (status === undefined) {
    return undefined
  }

  if (status === 0) {
    return "We're still setting up your session. Please try again in a moment."
  }

  if (status === 400 || status === 422) {
    return "Some information was invalid. Check your input and try again."
  }

  if (status === 401) {
    return "Your session has expired. Sign in again to continue."
  }

  if (status === 403) {
    return "You don't have permission to do that."
  }

  if (status === 404) {
    return "We couldn't find that item. It may have been deleted or you may not have access."
  }

  if (status === 409) {
    return "That item changed before we could save your update. Refresh and try again."
  }

  if (status === 429) {
    return "You're doing that a little too quickly. Wait a moment and try again."
  }

  if (status >= 500) {
    return "Coval is having trouble completing that request. Please try again in a moment."
  }

  return undefined
}

function isNetworkError(error: unknown) {
  const message = getErrorMessage(error)
  if (!message) {
    return false
  }

  return (
    message.includes("Failed to fetch") ||
    message.includes("Load failed") ||
    message.includes("NetworkError")
  )
}

function isAbortError(error: unknown) {
  return getErrorName(error) === "AbortError"
}

function getUserFacingErrorMessage(
  error: unknown,
  options: UserFacingErrorOptions = {},
) {
  if (isAbortError(error)) {
    return "The request was cancelled."
  }

  if (isNetworkError(error)) {
    return NETWORK_ERROR_MESSAGE
  }

  const fieldLabels = options.fieldLabels ?? {}

  return (
    getValidationDetailsMessage(error, fieldLabels) ??
    (options.detailMessage ? getDetailMessage(error) : undefined) ??
    getMessageForErrorCode(getDetailCode(error)) ??
    getMessageForStatus(getStatus(error)) ??
    options.fallback ??
    GENERIC_ERROR_MESSAGE
  )
}

export { getUserFacingErrorMessage }
export type { UserFacingErrorOptions }
